import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LayoutGrid } from 'lucide-react';
import client, { resolveApiAssetUrl } from '../../lib/api';
import { useGameLauncher } from '../../hooks/useGameLauncher';
import { useSettings } from '../../context/SettingsContext';
import { useVisibleHomeCategories } from '../../lib/homeCategories';
import { cacheSliders, readCachedSliders } from '../../lib/sliderCache';
import { slidersAt } from '../../lib/sliderPlacement';
import useSwipe from '../../hooks/useSwipe';
import GameModal from '../../components/GameModal';
import GameChoiceModal from '../../components/GameChoiceModal';
import AuthModal from '../../components/AuthModal';
import ProviderStrip, { providersFromResponse } from '../../components/ProviderStrip';
import TopWinsStrip from '../../components/TopWinsStrip';
import VegaSection from './VegaSection';
import VegaGameCard from './VegaGameCard';
import { resolveVegaShortcutLimit } from './vegaShortcuts';
import { SECTIONS, sectionHook, HOOKS, hook } from '../../lib/domHooks';
import HomeBlocks, { HomeBlock } from '../../components/HomeBlocks';

const STRIP_LIMIT = 14;
const SHORTCUT_LIMIT = 12;
// Referans arayüzde hero tek bannerdır ve altındaki noktalarla dönerdir.
const HERO_INTERVAL = 6000;

const mapGame = (game) => ({
  id: game.id,
  name: game.name,
  provider: game.provider,
  img: game.image,
  hot: game.hot,
  new: game.new,
  api_provider: game.api_provider,
  game_id: game.game_id,
  demo_supported: !!game.demo_supported,
});

const VegaHome = () => {
  const settings = useSettings();
  const gl = useGameLauncher();

  // Klasik vitrinle aynı önbellek: yanıt beklenirken son bannerlar çizilir,
  // taze liste gelince üzerine yazılır (bkz. lib/sliderCache.js).
  const [banners, setBanners] = useState(readCachedSliders);
  const [featured, setFeatured] = useState([]);
  const [popular, setPopular] = useState([]);
  const [fresh, setFresh] = useState([]);
  // Kategori şeritleri "+N" rozetini toplam oyun sayısından üretir; bu yüzden
  // yalnızca liste değil `total` da tutulur (`/games` yanıtı ikisini de verir).
  const [slots, setSlots] = useState({ items: [], total: 0 });
  const [live, setLive] = useState({ items: [], total: 0 });
  const [crash, setCrash] = useState({ items: [], total: 0 });
  const [providers, setProviders] = useState([]);
  const [heroPage, setHeroPage] = useState(0);
  const [shortcutLimit, setShortcutLimit] = useState(SHORTCUT_LIMIT);
  const shortcutStripRef = useRef(null);
  const visibleCategories = useVisibleHomeCategories(settings.sports_enabled);

  useEffect(() => {
    const get = (url, params) => client.get(url, { params }).then(({ data }) => data).catch(() => null);
    const asList = (data) => ({
      items: data?.items || [],
      total: Number(data?.total) || 0,
    });

    // Ağ hatasında (`data === null`) önbellekten çizilen bannerlar korunur.
    get('/sliders').then((data) => {
      if (!data) return;
      cacheSliders(data.items || []);
      setBanners(data.items || []);
    });
    get('/games/showcase', { strip: 'popular', category: 'slot', limit: STRIP_LIMIT })
      .then((data) => setPopular(data?.items || []));
    get('/games/showcase', { strip: 'new', category: 'slot', limit: STRIP_LIMIT })
      .then((data) => setFresh(data?.items || []));
    get('/games', { category: 'slot', limit: STRIP_LIMIT }).then((data) => setSlots(asList(data)));
    get('/games', { category: 'live-casino', limit: STRIP_LIMIT }).then((data) => setLive(asList(data)));
    get('/games', { category: 'crash', limit: STRIP_LIMIT }).then((data) => setCrash(asList(data)));
    get('/game-providers', { category: 'slot' }).then((data) => {
      setProviders(providersFromResponse(data));
    });
  }, []);

  // Yönetici seçimleri daima başta kalır; Vega karesinde kalan yerler açık
  // katalogdan rastgele tamamlanır. Limit masaüstünde şeridin gerçek genişliği
  // değiştikçe yeniden istenir, telefonda ise mevcut 12 oyun davranışı korunur.
  useEffect(() => {
    let active = true;
    client.get('/games/showcase', {
      params: {
        strip: 'featured', category: 'slot', limit: shortcutLimit, fill: 'random',
      },
    }).then(({ data }) => {
      if (active) setFeatured(data?.items || []);
    }).catch(() => {
      if (active) setFeatured([]);
    });
    return () => { active = false; };
  }, [shortcutLimit]);

  useEffect(() => {
    const strip = shortcutStripRef.current;
    if (!strip) return undefined;

    const measure = () => {
      const next = resolveVegaShortcutLimit(window.innerWidth, strip.clientWidth);
      setShortcutLimit((current) => (current === next ? current : next));
    };
    measure();

    if (typeof ResizeObserver === 'function') {
      const observer = new ResizeObserver(measure);
      observer.observe(strip);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [featured.length]);

  // Hero yalnızca görseli çizer (metin/CTA alanı yok); görselsiz banner burada
  // boş bir kutu olurdu, bu yüzden elenir.
  // Yalnızca ana slider banner'ları: 'side' ve katalog sayfası banner'ları
  // buraya düşmemelidir (bkz. lib/sliderPlacement.js).
  const visibleBanners = slidersAt(banners).filter(
    (banner) => (
      banner.image
      && (banner.cta_link !== '/spor' || settings.sports_enabled !== false)
    ),
  );
  const heroCount = visibleBanners.length;
  const activeHero = heroCount > 0 ? heroPage % heroCount : 0;
  const heroBanner = visibleBanners[activeHero];

  // Sayaç heroPage'e de bağlıdır: parmakla kaydıran oyuncu seçtiği banner'ı
  // tam süre görsün, otomatik geçiş kaydırmanın hemen ardına düşmesin.
  useEffect(() => {
    if (heroCount < 2) return undefined;
    const timer = setInterval(() => setHeroPage((page) => (page + 1) % heroCount), HERO_INTERVAL);
    return () => clearInterval(timer);
  }, [heroCount, heroPage]);

  const heroSwipe = useSwipe({
    enabled: heroCount > 1,
    onNext: () => setHeroPage((page) => (page + 1) % heroCount),
    onPrev: () => setHeroPage((page) => (page - 1 + heroCount) % heroCount),
  });

  return (
    <>
      <HomeBlocks theme="vega">
        <HomeBlock id={SECTIONS.HERO}>
        {heroBanner && (
          <div className="vega-hero" {...heroSwipe} {...sectionHook(SECTIONS.HERO)}>
            <Link
              key={activeHero}
              to={heroBanner.cta_link || '/promosyonlar'}
              className="vega-hero-item"
              {...hook(HOOKS.SLIDER)}
            >
              <img src={resolveApiAssetUrl(heroBanner.image)} alt={heroBanner.title || 'Kampanya'} />
            </Link>
            {/* Referansta banner'ın altında ince nokta kumandası durur; aktif
                nokta uzayıp altın çubuğa döner. Tek banner varken çizilmez. */}
            {heroCount > 1 && (
              <div className="vega-hero-dots">
                {visibleBanners.map((banner, index) => (
                  <button
                    key={banner.id || banner.image || index}
                    type="button"
                    className={`vega-hero-dot ${index === activeHero ? 'active' : ''}`}
                    onClick={() => setHeroPage(index)}
                    aria-label={`${index + 1}. banner`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.CATEGORIES}>
        {/* Referansın ana sayfa imzası: "Kategoriler" başlığı altında büyük
            ikonlu kare kutular. Kartlar panelden gelir; görseli olmayan kart
            boş bir kare yerine ikonla çizilir. */}
        {visibleCategories.length > 0 && (
          <div className="vega-cats" {...sectionHook(SECTIONS.CATEGORIES)}>
            <h2 className="vega-block-title">Kategoriler</h2>
            <div className="vega-cats-scroll">
              {visibleCategories.map((category) => (
                <Link
                  key={category.id}
                  to={category.href || '/'}
                  className="vega-cat"
                  {...hook(HOOKS.CATEGORY_CARD)}
                >
                  {category.image ? (
                    <img
                      className="vega-cat-art"
                      src={resolveApiAssetUrl(category.image)}
                      alt=""
                      aria-hidden="true"
                      loading="lazy"
                    />
                  ) : (
                    <span className="vega-cat-icon"><LayoutGrid size={32} /></span>
                  )}
                  <span className="vega-cat-label">{category.title}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.SHORTCUTS}>
        {/* Kategorilerin altındaki kare kısayollar panelin "öne çıkan"
            seçimidir; aşağıdaki şeritler kendi vitrin etiketlerinden gelir,
            böylece hiçbir şerit bir diğerinin kopyası olmaz. */}
        {featured.length > 0 && (
          <div className="vega-shortcut-strip" ref={shortcutStripRef} {...sectionHook(SECTIONS.SHORTCUTS)}>
            {featured.map((game) => (
              <VegaGameCard
                key={game.id}
                game={mapGame(game)}
                onPlay={gl.play}
                variant="tile"
              />
            ))}
          </div>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.POPULAR}>
        {popular.length > 0 && (
          <VegaSection section={SECTIONS.POPULAR} title="Popüler Oyunlar" to="/slotlar">
            {popular.map((game) => (
              <VegaGameCard key={game.id} game={mapGame(game)} onPlay={gl.play} />
            ))}
          </VegaSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.SLOTS}>
        {slots.items.length > 0 && (
          <VegaSection
            section={SECTIONS.SLOTS}
            title="Slot Oyunları"
            to="/slotlar"
            count={slots.total}
            shown={slots.items.length}
          >
            {slots.items.map((game) => (
              <VegaGameCard key={game.id} game={mapGame(game)} onPlay={gl.play} />
            ))}
          </VegaSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.LIVE_CASINO}>
        {live.items.length > 0 && (
          <VegaSection
            section={SECTIONS.LIVE_CASINO}
            title="Canlı Casino"
            to="/canli-casino"
            count={live.total}
            shown={live.items.length}
          >
            {live.items.map((game) => (
              <VegaGameCard key={game.id} game={mapGame(game)} onPlay={gl.play} />
            ))}
          </VegaSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.NEW}>
        {fresh.length > 0 && (
          <VegaSection section={SECTIONS.NEW} title="Yeni Oyunlar" to="/slotlar">
            {fresh.map((game) => (
              <VegaGameCard key={game.id} game={mapGame(game)} onPlay={gl.play} />
            ))}
          </VegaSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.FAST}>
        {crash.items.length > 0 && (
          <VegaSection
            section={SECTIONS.FAST}
            title="Hızlı Oyunlar"
            to="/crash"
            count={crash.total}
            shown={crash.items.length}
          >
            {crash.items.map((game) => (
              <VegaGameCard key={game.id} game={mapGame(game)} onPlay={gl.play} />
            ))}
          </VegaSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.WINS}>
        {/* Katalog sayfalarındaki "Yüksek Kazançlar" şeridinin aynısı. Kaynak
            tek uçtur (`/games/recent-wins?scope=high`) ve kategoriye göre
            süzülmez: slot, canlı casino, hızlı oyun ve hazır spor kitabının
            turları aynı listede karışık akar. Sayfalardaki şeridin davranışı
            değişmez — burada da aynı rastgele demet gösterilir. */}
        <TopWinsStrip />
        </HomeBlock>

        <HomeBlock id={SECTIONS.PROVIDERS}>
        <ProviderStrip providers={providers} classPrefix="vega" />
        </HomeBlock>
      </HomeBlocks>

      <GameChoiceModal
        game={gl.choice}
        user={gl.user}
        balance={gl.balance}
        bonusBalance={gl.bonusBalance}
        demoAvailable={gl.demoAvailable}
        onClose={gl.closeChoice}
        onReal={gl.startReal}
        onDemo={gl.startDemo}
        onLogin={() => gl.openAuth('login')}
        onRegister={() => gl.openAuth('register')}
      />
      {gl.activeGame && (
        <GameModal
          key={`${gl.activeGame.id}-${gl.mode}`}
          game={gl.activeGame}
          mode={gl.mode}
          onClose={gl.closeGame}
        />
      )}
      <AuthModal
        open={!!gl.authTab}
        onClose={() => gl.setAuthTab(null)}
        defaultTab={gl.authTab || 'login'}
      />
    </>
  );
};

export default VegaHome;
